<template>
  <div class="diagnostic-log-viewer">
    <div class="log-header">
      <span class="log-header-title"><mrx-translation token="app.device.logs.title" /></span>
      <span class="live-indicator" :class="{ 'live-active': isEnabled }">
        <span class="live-dot" />
        <mrx-translation :token="isEnabled ? 'app.device.logs.live_feed' : 'app.device.logs.paused'" />
      </span>
    </div>

    <div class="log-controls">
      <v-btn
        id="toggle-diagnostic-logging"
        :color="isEnabled ? 'error' : 'success'"
        small
        depressed
        class="log-control-btn"
        @click="toggleLogging"
      >
        <span class="log-btn-content">
          <v-icon left size="16">{{ isEnabled ? icons.mdiPause : icons.mdiPlay }}</v-icon>
          <mrx-translation :token="isEnabled ? 'app.device.logs.btn.pause' : 'app.device.logs.btn.enable'" />
        </span>
      </v-btn>
      <v-btn
        v-if="logs.length > 0"
        id="clear-diagnostic-logs"
        small
        depressed
        color="grey darken-2"
        class="log-control-btn white--text ml-2"
        @click="clearLogs"
      >
        <span class="log-btn-content">
          <v-icon left size="16">{{ icons.mdiTrashCanOutline }}</v-icon>
          <mrx-translation token="app.device.logs.btn.clear" />
        </span>
      </v-btn>
    </div>

    <div ref="logContainer" class="log-entries">
      <div v-if="logs.length === 0" class="log-empty">
        <v-icon size="48" color="grey darken-1">{{ icons.mdiInformationOutline }}</v-icon>
        <div class="log-empty-text"><mrx-translation token="app.device.logs.empty.title" /></div>
        <div v-if="!isEnabled" class="log-empty-hint"><mrx-translation token="app.device.logs.empty.subtitle" /></div>
      </div>

      <transition-group v-else name="log-item" tag="div">
        <div
          v-for="log in reversedLogs"
          :key="log.id"
          class="log-entry"
          :class="'log-entry--' + log.level.toLowerCase()"
        >
          <div class="log-entry-header">
            <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
            <span class="log-level-badge" :class="'badge--' + log.level.toLowerCase()">
              {{ log.level }}
            </span>
          </div>
          <div class="log-entry-meta">
            <span class="log-category">{{ capitalise(log.category) }}</span>
          </div>
          <div class="log-entry-body">
            <span class="log-event">{{ parseEventKey(log.event).label }}</span>
            <span v-if="log.message" class="log-details">{{ log.message }}</span>
          </div>
        </div>
      </transition-group>
    </div>

    <div v-if="logs.length > 0" class="log-footer">
      <span class="log-count">
        <mrx-translation :prepend="logs.length.toString() + ' '" :token="logs.length !== 1 ? 'app.device.logs.count.plural' : 'app.device.logs.count.singular'" />
      </span>
    </div>
  </div>
</template>

<script>
import icons from '@/mixins/icons'
import { mapGetters } from 'vuex'
import { capitalise, parseEventKey } from "@/utils/textUtils";

export default {
  name: 'DiagnosticLogViewer',
  mixins: [icons],
  data() {
    return {
      _pollTimer: null
    }
  },
  computed: {
    ...mapGetters({
      logs: 'v4/diagnosticLog/all',
      isEnabled: 'v4/diagnosticLog/isEnabled',
    }),
    // Display logs in reverse-chronological order (newest first)
    reversedLogs() {
      return [...this.logs].reverse()
    }
  },
  async mounted() {
    await this.$store.dispatch('v4/diagnosticLog/loadLogs')
    if (this.isEnabled) {
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    parseEventKey,
    capitalise,
    async toggleLogging() {
      const newEnabled = !this.isEnabled
      await this.$store.dispatch('v4/diagnosticLog/setEnabled', newEnabled)
      await this.$store.dispatch('v4/diagnosticLog/loadLogs')

      if (newEnabled) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    async clearLogs() {
      await this.$store.dispatch('v4/diagnosticLog/clearLogs')
    },
    formatTimestamp(isoString) {
      const date = new Date(isoString)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')
      const millis = date.getMilliseconds().toString().padStart(3, '0')
      return `${hours}:${minutes}:${seconds}.${millis}`
    },
    startPolling() {
      this.stopPolling()
      this._pollTimer = setInterval(async () => {
        await this.$store.dispatch('v4/diagnosticLog/loadLogs')
      }, 2000)
    },
    stopPolling() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer)
        this._pollTimer = null
      }
    }
  }
}
</script>

<style scoped>
.diagnostic-log-viewer {
  width: 100%;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  margin-bottom: 4px;
}

.log-header-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}

.live-indicator.live-active {
  color: #00e676;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
}

.live-indicator.live-active .live-dot {
  background-color: #00e676;
  box-shadow: 0 0 8px rgba(0, 230, 118, 0.6);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(0, 230, 118, 0.6); }
  50% { opacity: 0.5; box-shadow: 0 0 16px rgba(0, 230, 118, 0.3); }
}

.log-controls {
  display: flex;
  align-items: center;
  padding: 0 24px 12px;
}

.log-control-btn {
  text-transform: none !important;
  font-weight: 600;
  font-size: 12px !important;
  letter-spacing: 0.3px;
  border-radius: 8px !important;
}

.log-btn-content {
  display: flex;
  align-items: center;
}

.log-btn-content .v-icon {
  transform: translateY(-1px);
}

.log-btn-content mrx-translation {
  transform: translateY(1px);
}

.log-entries {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px;
  scroll-behavior: smooth;
  scrollbar-width: none; 
}

.log-entries::-webkit-scrollbar {
  display: none; 
}

.log-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.log-empty-text {
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 500;
}

.log-empty-hint {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
}

.log-entry {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  border-left: 3px solid transparent;
  padding: 12px 16px;
  margin-bottom: 8px;
  transition: background 0.2s ease, transform 0.2s ease;
}

.log-entry:hover {
  background: rgba(255, 255, 255, 0.07);
}

.log-entry--info {
  border-left-color: #00e676;
}

.log-entry--warn {
  border-left-color: #ffa726;
}

.log-entry--error {
  border-left-color: #ef5350;
  background: rgba(239, 83, 80, 0.06);
}

.log-entry--error:hover {
  background: rgba(239, 83, 80, 0.1);
}

.log-entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.log-timestamp {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
}

.log-level-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.badge--info {
  background: rgba(0, 230, 118, 0.15);
  color: #00e676;
}

.badge--warn {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

.badge--error {
  background: rgba(239, 83, 80, 0.15);
  color: #ef5350;
}

.log-entry-meta {
  margin-bottom: 4px;
}

.log-category {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.5px;
}

.log-entry-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.log-event {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.log-details {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
  word-break: break-word;
}

.log-footer {
  display: flex;
  justify-content: center;
  padding: 8px 24px;
}

.log-count {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.5px;
}

.log-item-enter-active {
  transition: all 0.3s ease-out;
}
.log-item-leave-active {
  transition: all 0.2s ease-in;
}
.log-item-enter {
  opacity: 0;
  transform: translateY(-10px);
}
.log-item-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.log-item-move {
  transition: transform 0.3s ease;
}
</style>
